"use client"

import React, { useMemo, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { SearchParams } from "@ub/fact-finder-client"
import { XCircleIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { FacetData } from "@/lib/factfinder/factfinder-interfaces"
import { getResetLinkToSearchResultOrCategory } from "@/lib/link-utils/link-utils"
import { extractFacetsToHide } from "@/lib/sb-helper"
import { Category } from "@/lib/storefront/category/category"
import { AccordionContent, AccordionItem, AccordionRoot, AccordionTrigger, Button } from "@/components/ui/components"
import { extractAttributeNameFromFFFieldName, shouldRenderAsCustomFacet } from "@/components/ui/helpers/filterHelper"
import { cn } from "@/components/ui/helpers/utils"
import CategoryTreeClient from "@/components/search-and-category/CategoryTreeClient"
import { CustomFilterWrapper } from "@/components/search-and-category/customfilter/CustomFilterWrapper"
import { buildCustomFilterAttributesMap } from "@/components/search-and-category/customfilter/filterHelper"
import FacetClient from "@/components/search-and-category/FacetClient"
import { CategoryContentStoryblok, CustomFilterStoryblok } from "@/components/storyblok/component-types"

import { FacetForBrandClient } from "./FacetForBrandClient"
import { FacetForPriceClient } from "./FacetForPriceClient"

interface FacetListProps {
  facetManager: FacetPickerResultData | undefined
  defaultFacetsToHide: string[]
  facets: FacetData[]
  query?: string
  baseCategory?: Category
  brandSlug?: string
  categoryAsTree?: boolean
  categoryOnly?: boolean
  isFiltered: boolean
  hitcount: number
  categoryContent?: CategoryContentStoryblok
  customFilterEnabled?: boolean
  useFacetConfiguration?: boolean
  embedMode?: boolean
  ffSearchParams: SearchParams | undefined
  isoLocale: string
}

export interface FacetConfigData {
  name: string
  fieldName: string
  localizedFieldNames?: Record<string, string>
  active: boolean
  position: number
}

export interface FacetPickerResultData {
  id: string
  name: string
  image?: string
  type: string
  environment: string
  query?: string
  link: string
  sbLocale: string
  facetConfigActive: boolean
  facetConfigData: FacetConfigData[]
}

// see ub-intershop-intern: FilterList.isml
const FACET_COUNT_TO_SHOW_IN_SEARCH = 11
const FACET_COUNT_TO_SHOW_IN_CATEGORIES = 12
const FACET_COUNT_TO_SHOW_EXPANDED = 4

function categoryContentCustomFilters(categoryContent: CategoryContentStoryblok | undefined, query?: string) {
  return categoryContent &&
    categoryContent?.customFilterSpace &&
    categoryContent?.customFilterSpace[0]?.customFilterList
    ? categoryContent?.customFilterSpace[0]?.customFilterList
    : undefined
}

function findFacetConfigDataByFieldName(facetConfigData: any, fieldName: string, isoLocale: string) {
  return facetConfigData.find((fcd: any) => {
    const language: string = isoLocale ? isoLocale.split("-")[0] : "de"
    const translatedFieldName = fcd.localizedFieldNames?.[language] ?? fcd.fieldName
    return translatedFieldName.toLocaleLowerCase() === fieldName.toLocaleLowerCase()
  })
}

function checkFacetConfiguration(
  useFacetConfiguration: boolean,
  facetPickerResultData: FacetPickerResultData | undefined,
  fieldName: string
) {
  /*  const facetConfigData = facetPickerResultData?.facetConfigData

    console.dir(facetConfigData)
    if (!useFacetConfiguration || !facetPickerResultData?.facetConfigActive) {
      return true
    } else {
      if (facetConfigData && facetConfigData.length > 0) {
        const foundFacetConfigData = findFacetConfigDataByFieldName(facetConfigData, fieldName)
        console.log(fieldName + " checkend " + foundFacetConfigData)

        return foundFacetConfigData !== undefined
      }
    }*/
  return true
}

function sortFacetsConsideringCustomFilters(
  customFilterEnabled: boolean,
  categoryContent: CategoryContentStoryblok | undefined,
  facets: FacetData[],
  defaultFacetsToHide: string[],
  keepCustom: boolean,
  useFacetConfiguration: boolean,
  isoLocale: string
) {
  const facetPickerResultData: FacetPickerResultData | undefined = categoryContent?.facetManager

  const customFilters: CustomFilterStoryblok[] | undefined = customFilterEnabled
    ? categoryContentCustomFilters(categoryContent)
    : []

  const hasCustomFilters = customFilters && customFilters.length > 0

  //The facets that should be invisible when rendering in custom mode
  const customFacetsToHide: string[] = categoryContent?.facetsToHide
    ? extractFacetsToHide(categoryContent?.facetsToHide)
    : []
  //console.dir(customFacetsToHide)
  //defaultFacetsToHide - The facets that should be invisible when rendering in normal mode
  const markedFacets: string[] = hasCustomFilters && customFilterEnabled ? customFacetsToHide : defaultFacetsToHide //Init with explicitly defined facets to hide
  const customFacets: string[] = []
  const sortedFacets: FacetData[] = []

  //iterate over custom filters, if they exist then add their attribute names to the list of marked attributes
  if (hasCustomFilters && customFilterEnabled) {
    //Iterate over custom filters
    for (const filter of customFilters) {
      //Find the facet for the custom filter
      const facetForCustomFilter = facets.find((facetData: FacetData) => {
        return (
          filter?.active &&
          extractAttributeNameFromFFFieldName(facetData.fieldName) === filter?.attribute?.toLowerCase()
        )
      })
      //If the facet is found then...
      if (facetForCustomFilter) {
        // ...check if custom facet should be kept -> add to customFacets
        if (keepCustom) {
          customFacets.push(filter?.attribute?.toLowerCase() ?? "")
          // ... otherwise mark them
        } else {
          markedFacets.push(filter?.attribute?.toLowerCase() ?? "")
        }
      }
    }
  }

  //Iterate over all customFacetFields
  for (const facetFieldName of customFacets) {
    const facetForCustomFilter = facets.find((facetData: FacetData) => {
      return extractAttributeNameFromFFFieldName(facetData.fieldName) === facetFieldName
    })

    if (facetForCustomFilter) {
      sortedFacets.push(facetForCustomFilter)
    }
  }

  //Exclude the _txt facets - but only if not using explicit facet configuration

  const FILTER_EXCLUSION_PATTERN = "_txt"
  const shouldExcludeByPattern = (facetFieldName: string) => {
    return !!(facetFieldName && facetFieldName.length > 0 && facetFieldName.includes(FILTER_EXCLUSION_PATTERN))
  }

  //Iterate again over all facets checking visibility and set position
  let position = 0
  //console.dir(facets)
  for (const facet of facets) {
    const facetFieldName = extractAttributeNameFromFFFieldName(facet.fieldName)
    //Push to other facets if not marked and not already in customFacets
    //console.dir(facetFieldName)
    if (
      facet &&
      !markedFacets.includes(facetFieldName) &&
      !customFacets.includes(facetFieldName) &&
      (useFacetConfiguration && facetPickerResultData?.facetConfigActive
        ? true
        : !shouldExcludeByPattern(facetFieldName)) &&
      //Considering facetConfiguration
      checkFacetConfiguration(useFacetConfiguration, facetPickerResultData, facet.fieldName)
    ) {
      position++

      const facetWithPosition = { ...facet } as FacetData
      facetWithPosition.position = position

      if (
        useFacetConfiguration &&
        facetPickerResultData?.facetConfigData &&
        facetPickerResultData?.facetConfigData.length > 0
      ) {
        const facetConfigData: any = findFacetConfigDataByFieldName(
          facetPickerResultData?.facetConfigData,
          facet.fieldName,
          isoLocale
        )
        if (facetPickerResultData?.facetConfigActive && facetConfigData && facetConfigData.position) {
          facetWithPosition.position = facetConfigData.position
        } else {
          facetWithPosition.position = position + 1000
        }
      }
      sortedFacets.push(facetWithPosition)
    }
  }

  sortedFacets.sort((a, b) => ((a.position ?? 0) > (b.position ?? 0) ? 1 : -1))

  //console.dir(sortedFacets)

  return sortedFacets
}

export default function FacetList(props: FacetListProps) {
  const t = useTranslations("storefront")
  const [showAll, setShowAll] = useState(false)
  const { filterShown, setFilterShown } = useUbStorefrontState()

  const customFilterEnabled: boolean = useMemo(() => {
    return props.customFilterEnabled ?? false
  }, [props.customFilterEnabled])

  const sortedFacets: FacetData[] = useMemo(() => {
    return sortFacetsConsideringCustomFilters(
      customFilterEnabled,
      props.categoryContent,
      props.facets,
      props.defaultFacetsToHide,
      !(props.embedMode ?? false),
      props.useFacetConfiguration ?? false,
      props.isoLocale
    )
  }, [
    customFilterEnabled,
    props.categoryContent,
    props.defaultFacetsToHide,
    props.embedMode,
    props.facets,
    props.useFacetConfiguration,
    props.isoLocale,
  ])

  const customFilterAttributes: Record<string, CustomFilterStoryblok> = useMemo(() => {
    return buildCustomFilterAttributesMap(customFilterEnabled, categoryContentCustomFilters(props.categoryContent))
  }, [customFilterEnabled, props.categoryContent])

  const linkToResetAllFilters: string = useMemo(() => {
    return getResetLinkToSearchResultOrCategory(props.query, props.baseCategory, props.brandSlug, props.isoLocale)
  }, [props.baseCategory, props.brandSlug, props.isoLocale, props.query])

  const openFacets: string[] = useMemo(() => {
    const firstNonCustomFacets: string[] = []
    for (const facet of sortedFacets) {
      if (!customFilterAttributes[extractAttributeNameFromFFFieldName(facet.fieldName)]) {
        firstNonCustomFacets.push(facet.name)
      }
    }
    return firstNonCustomFacets
  }, [customFilterAttributes, sortedFacets])

  const hasAnyFacets: boolean = useMemo(() => {
    return !!sortedFacets?.length
  }, [sortedFacets?.length])

  const needResetFilterButton: boolean = useMemo(() => {
    let nr = !hasAnyFacets || props.isFiltered

    if (props.hitcount <= 0 && !props.isFiltered) {
      nr = false
    }
    return nr
  }, [hasAnyFacets, props.hitcount, props.isFiltered])

  const initialFacetCountToShow: number = useMemo(() => {
    return props.baseCategory
      ? props.useFacetConfiguration && (props.categoryContent?.facetManager?.facetConfigActive ?? false)
        ? (props.categoryContent?.facetManager?.facetConfigData?.length ?? FACET_COUNT_TO_SHOW_IN_CATEGORIES)
        : FACET_COUNT_TO_SHOW_IN_CATEGORIES
      : FACET_COUNT_TO_SHOW_IN_SEARCH
  }, [props.baseCategory, props.categoryContent, props.useFacetConfiguration])

  const hiddenCount: number = useMemo(() => {
    return (sortedFacets?.length || 0) - initialFacetCountToShow
  }, [initialFacetCountToShow, sortedFacets?.length])

  const facetCountToShow: number = useMemo(() => {
    return hiddenCount <= 1 ? Infinity : initialFacetCountToShow
  }, [hiddenCount, initialFacetCountToShow])

  function handleCloseFilter() {
    setFilterShown(false)
  }

  return (
    <div
      className={cn("mb-24 hidden lg:block", {
        block: filterShown,
      })}
    >
      <pre className={"hidden"}>{JSON.stringify(props.facetManager)}</pre>
      <div
        className={cn(
          "fixed inset-x-0 top-0 hidden h-12 items-center justify-center border-b border-b-gray-200 bg-white",
          {
            "flex z-[301] lg:hidden": filterShown,
          }
        )}
      >
        <p className={"text-lg font-bold"}> {t("common.filter")}</p>
        <XCircleIcon
          className={cn("absolute right-0 top-1 m-2 hidden size-6 cursor-pointer bg-white lg:hidden", {
            block: filterShown,
          })}
          onClick={handleCloseFilter}
        />
      </div>

      <div
        className={cn(
          "fixed inset-x-0 bottom-32 top-12 hidden bg-ub-background lg:static lg:block lg:overflow-y-auto",
          {
            "block z-[300]  overflow-y-scroll": filterShown,
          }
        )}
      >
        <div className="relative flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            {needResetFilterButton && !hasAnyFacets && (
              <a className="mb-4 hidden lg:block " href={linkToResetAllFilters}>
                {t("common.reset_filters")}
              </a>
            )}

            {hasAnyFacets && (
              <AccordionRoot
                className={cn("min-w-80 max-w-80 pt-2", {
                  "max-w-full pt-0": filterShown || props.embedMode,
                  "pl-4 pr-2": props.embedMode,
                })}
                type="multiple"
                defaultValue={openFacets}
              >
                {needResetFilterButton && (
                  <a className="mb-4 hidden lg:block" href={linkToResetAllFilters}>
                    {t("common.reset_filters")}
                  </a>
                )}

                <div>
                  {sortedFacets &&
                    sortedFacets.map((facet: FacetData, index: number) => (
                      <div
                        key={index}
                        className={
                          ((index < facetCountToShow || showAll) && !props.categoryOnly) ||
                          (props.categoryOnly && index === 0)
                            ? "block"
                            : "hidden"
                        }
                      >
                        {!shouldRenderAsCustomFacet(facet, customFilterAttributes) ? (
                          <>
                            <FacetListItem index={index} facet={facet} {...props} />
                          </>
                        ) : (
                          <>
                            {filterShown && (
                              <div
                                data-attribute={facet.fieldName}
                                className="mb-4 rounded-sm border border-gray-200 bg-white p-2 font-bold"
                              >
                                <CustomFilterWrapper
                                  isoLocale={props.isoLocale}
                                  customFilter={
                                    customFilterAttributes[extractAttributeNameFromFFFieldName(facet.fieldName)]
                                  }
                                  query={props.query}
                                  baseCategory={props.baseCategory}
                                  facetData={facet}
                                  ffSearchParams={props.ffSearchParams}
                                  overlay={true}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  <div
                    className={cn("block", {
                      hidden: props.categoryOnly,
                    })}
                  >
                    {sortedFacets && sortedFacets.length > facetCountToShow && (
                      <button
                        className="my-2 w-full border border-gray-200 bg-white p-2 text-xs text-gray-500 lg:p-4"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? t("common.show_less") : t("common.show_further_count", { count: hiddenCount })}
                      </button>
                    )}
                  </div>
                </div>
              </AccordionRoot>
            )}
          </div>
        </div>
      </div>
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 hidden h-32 flex-col items-center justify-center gap-2 border-t border-t-gray-200 bg-white px-8 ",
          {
            "flex z-[300] lg:hidden": filterShown,
          }
        )}
      >
        <Button className={"w-full"} size={"lg"} onClick={handleCloseFilter}>
          {t("common.apply_filters", { hitcount: props.hitcount })}
        </Button>

        <a className={"mb-4 block w-full"} href={linkToResetAllFilters}>
          <Button className={"w-full"} size={"lg"} variant={needResetFilterButton && !hasAnyFacets ? "ghost" : "black"}>
            {t("common.reset_filters")}
          </Button>
        </a>
      </div>
    </div>
  )
}

interface FacetListItemProps {
  index: number
  facet: FacetData
  query?: string
  baseCategory?: Category
  brandSlug?: string
  categoryAsTree?: boolean
  isoLocale: string
}

/*Could be
 a FacetForBrand filter OR
 a FacetForPrice filter OR
 a CategoryTree filter OR
 a default Facet filter*/
function FacetListItem({ index, facet, ...props }: FacetListItemProps) {
  const isBrand = facet.fieldName === "brand"
  const isPrice = facet.fieldName === "price"
  const isCategory = facet.fieldName === "category"
  const showCategoryTree = props.categoryAsTree && isCategory
  const t = useTranslations("storefront")

  return (
    <AccordionItem
      data-attribute={facet.fieldName + "-" + facet.position}
      key={index}
      className="mb-4 rounded-sm border border-gray-200 bg-white p-2 font-bold"
      value={facet.name}
    >
      <AccordionTrigger className="h-10 px-1 text-start shadow-none">
        {isCategory ? t("suggestion.result.header.categories") : (facet.name ?? "").replace("_TXT", "")}
      </AccordionTrigger>
      <AccordionContent className="text-sm font-normal">
        {isBrand && (
          <FacetForBrandClient
            isoLocale={props.isoLocale}
            query={props.query}
            baseCategory={props.baseCategory}
            brandSlug={props.brandSlug}
            facetData={facet}
          />
        )}
        {isPrice && (
          <FacetForPriceClient
            isoLocale={props.isoLocale}
            query={props.query}
            baseCategory={props.baseCategory}
            brandSlug={props.brandSlug}
            facetData={facet}
          />
        )}
        {showCategoryTree && props.baseCategory && (
          <CategoryTreeClient category={props.baseCategory} facet={facet} sbLocale={props.isoLocale} />
        )}{" "}
        {/*TODO sbLocale!=isoLocale*/}
        {!isBrand && !isPrice && !showCategoryTree && (
          <FacetClient
            isoLocale={props.isoLocale}
            query={props.query}
            baseCategory={props.baseCategory}
            brandSlug={props.brandSlug}
            facetData={facet}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  )
}
